// src/components/Tooltip.jsx
//
// Tooltip — shared hover-triggered dark popup (TOOLTIP-STANDARD).
//
// Presentational only: the caller decides when to render it (usually via
// useHoverIntent controller on the trigger element) and is responsible
// for positioning it relative to a `position: relative` ancestor by passing
// `style` (top/left/right/bottom/transform, etc). Tooltip supplies the
// canonical dark surface (T.tooltip) — background, text color, radius,
// shadow, and sensible type defaults — so bespoke tooltips no longer need to
// hand-roll their own white/light card styling.
//
// Usage:
//   const { open, triggerProps } = useHoverIntent();
//   <span {...triggerProps}>
//     trigger
//     {open && (
//       <Tooltip data-testid="my-tooltip" style={{ top: "100%", left: 0 }}>
//         Tooltip copy
//       </Tooltip>
//     )}
//   </span>
//
// Props spread onto the rendered <span>:
//   children — tooltip content
//   style    — positioning + any style overrides merged over the defaults
//   …rest    — any other props (role, data-testid, onMouseEnter, etc.)

import React from "react";
import { T } from "../app/tokens";

// Only explicit newlines mark paragraph boundaries; visual line wrapping does not.
export function TooltipParagraphs({ children }) {
  if (typeof children !== "string" || !/\r?\n/.test(children)) return children;

  return children.split(/\r?\n/).map((paragraph, index) => (
    <span key={index} style={{ display: "block", ...(index > 0 ? { marginTop: "1.5em" } : {}) }}>
      {paragraph}
    </span>
  ));
}

export const Tooltip = React.forwardRef(function Tooltip({ children, style, role = "tooltip", ...rest }, ref) {
  return (
    <span
      ref={ref}
      role={role}
      {...rest}
      style={{
        position: "absolute",
        zIndex: 30,
        background: T.tooltip.bg,
        color: T.tooltip.fg,
        borderRadius: 6,
        padding: "6px 10px",
        boxShadow: T.tooltip.shadow,
        fontSize: T.tooltip.fontSize,
        fontWeight: T.tooltip.fontWeight,
        lineHeight: T.tooltip.lineHeight,
        whiteSpace: "nowrap",
        pointerEvents: "none",
        ...style,
      }}
    >
      <TooltipParagraphs>{children}</TooltipParagraphs>
    </span>
  );
});
